import { Divider, FlexBox, useScreenClass } from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import { useLineItemActions } from '../contexts/LineItemActionsContext';
import { ItemEditDesignAndReorderCta } from '../shared/reorder/ItemEditDesignAndReorderCta';
import { ItemAddToCartButton } from '../shared/reorder/ItemAddToCartButton';
import { LineItemReorderActionFeedback } from '../shared/reorder/LineItemReorderActionFeedback';
import { DesignFeedbackBanner } from '../orderHistoryTab/designFeedback/DesignFeedbackBanner';
import { useLineItemData } from '../contexts/LineItemDataContext';
import { ItemBuyNowButton } from '../shared/ItemBuyNowButton';
import { ORDER_HISTORY_PAGE_NAME } from '../../constants/tracking';

/**
 * Displays the line item actions for the line item.
 * Actions may include buttons for buy now, reorder, edit and reorder.
 * Also displays the design feedback banner under certain conditions.
 * Takes no props, as it gets all necessary data from the LineItemActionsContext.
 * @returns {ReactElement | null} The line item actions; or null if no actions are enabled.
 */
export const LineItemActions = () => {
  const screenClass = useScreenClass();

  const {
    buyNowActionEnabled,
    designFeedbackActionEnabled,
    editAndReorderActionEnabled,
    fulfillmentHistoryEnabledForLineItem,
    isGroupItem,
    reorderActionEnabled,
  } = useLineItemActions();

  const isMobile = ['xs', 'sm'].includes(screenClass);
  const buttonWidth = isMobile ? 'full-width' : 'standard';

  const { id } = useLineItemData();
  // this really shouldn't happen, but ts doesn't know that
  if (!id) {
    return null;
  }

  if (
    !buyNowActionEnabled &&
    !designFeedbackActionEnabled &&
    !editAndReorderActionEnabled &&
    !reorderActionEnabled
  ) {
    return null;
  }

  if (designFeedbackActionEnabled && !fulfillmentHistoryEnabledForLineItem) {
    // Here, display banner at item level only for non-group items.
    if (!isGroupItem) {
      // design feedback banner
      return (
        <>
          <Divider my={3} />
          <DesignFeedbackBanner lineItemId={id} />
          <Divider my={3} />
        </>
      );
    }
    return <Divider my={3} />;
  }

  return (
    <div data-testid="line-item-actions" className="no-print">
      <Divider my={3} />
      <FlexBox
        flexWrap="wrap"
        flexDirection={isMobile ? 'column' : 'row'}
        gap="between-actions"
      >
        <ModTarget name="reorderButton">
          {/* currently assume we are on the order history page if we show buy now here */}
          {/* this would work if shown in the details page, but tracking values would be incorrect */}
          {buyNowActionEnabled ? (
            <ItemBuyNowButton pageName={ORDER_HISTORY_PAGE_NAME} />
          ) : null}
          {reorderActionEnabled ? (
            <ItemAddToCartButton
              buttonSkin={buyNowActionEnabled ? 'secondary' : 'primary'}
              buttonWidth={buttonWidth}
            />
          ) : null}
          {editAndReorderActionEnabled ? (
            <ItemEditDesignAndReorderCta buttonWidth={buttonWidth} />
          ) : null}
        </ModTarget>
      </FlexBox>
      <LineItemReorderActionFeedback />
      <Divider my={3} />
    </div>
  );
};
